import { findBotModel } from '@chess/core'
import { useEffect, type JSX } from 'react'
import { GearIcon } from '../lib/AccountMenu.js'
import { useEngineRoom } from '../room/connection.js'
import { Banner } from '../room/Overlays.js'
import { RoomView } from '../room/RoomView.js'
import { resolveTimeControl } from '../lib/timeControls.js'

export interface EngineGameProps {
  readonly timeControlId: string
  readonly modelId: string
  readonly navigate: (to: string) => void
  readonly onSettings: () => void
}

/**
 * A game against Stockfish, entirely in this browser.
 *
 * The engine runs as a Web Worker; nothing touches the server, which is why
 * these games work offline and never appear in the archive or your history.
 */
export const EngineGame = ({
  timeControlId,
  modelId,
  navigate,
  onSettings,
}: EngineGameProps): JSX.Element => {
  const model = findBotModel(modelId)
  const valid = model?.kind === 'engine'

  // A mistyped or stale URL goes home rather than to a broken board.
  useEffect(() => {
    if (!valid) navigate('/')
  }, [valid, navigate])

  const connection = useEngineRoom(resolveTimeControl(timeControlId), valid ? modelId : 'sf-1350')

  if (!valid) return <></>

  return (
    <RoomView
      connection={connection}
      mySlot={0}
      header={
        <header className="flex items-center justify-between px-2 py-2 safe-top sm:px-0">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-sm font-semibold text-ink-dim hover:text-ink"
          >
            ← Home
          </button>
          <span className="flex items-center gap-1">
            <span className="font-mono text-xs text-ink-dim">local</span>
            <button
              type="button"
              onClick={onSettings}
              aria-label="Settings"
              className="rounded-full p-1.5 text-ink-dim hover:text-ink"
            >
              <GearIcon />
            </button>
          </span>
        </header>
      }
      notice={
        connection.engineFailed ? (
          <Banner tone="warn">
            The engine could not be loaded. Check your connection and reload — or play one of the
            other computers, which run on the server.
          </Banner>
        ) : (
          <Banner>
            Playing {model.label}. {model.note} This game lives in your browser and is not saved.
          </Banner>
        )
      }
    />
  )
}
